// FIXED SongRequestForm.js - Complete implementation with customer name fix and reordered layout + Legal Integration

import React, { useState } from 'react';
import { db } from '../../config/firebase-config';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import StripePaymentForm from './StripePaymentForm.jsx';

function SongRequestForm({ musicianId, musicianName, preSelectedSong, onPaymentAuthorized }) {
  const [message, setMessage] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);

  // Base amount only (tip will be added during rating)
  const calculatedTotal = preSelectedSong?.price || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerName.trim()) {
      alert('Please enter your name');
      return;
    }

    if (customerName.trim().length < 2) {
      alert('Please enter a valid name (at least 2 characters)');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const calculatedTotal = preSelectedSong?.price || 0; // No tip at this stage
      setTotalAmount(calculatedTotal);
      
      console.log('🎵 Creating payment intent with customer name:', customerName.trim());
      
      // Create request data to save locally first (status: preparing)
      const requestData = {
        musicianId,
        musicianName,
        songId: preSelectedSong?.id,
        songName: preSelectedSong?.name,
        artist: preSelectedSong?.artist,
        songPrice: preSelectedSong?.price || 0,
        tip: 0, // Tip will be added during rating
        amount: calculatedTotal,
        customerName: customerName.trim(), // IMPORTANT: Trimmed customer name
        message: message.trim(),
        status: 'preparing', // Not 'pending' - no SMS sent yet
        timestamp: new Date(),
        paymentStatus: 'preparing',
        // Legal compliance tracking
        legalCompliance: {
          userConsentedToTerms: true,
          userConsentedToPrivacy: true,
          consentTimestamp: new Date(),
          dataProcessingBasis: 'user_consent',
          personalDataShared: {
            customerName: true,
            message: message.trim() ? true : false,
            paymentInfo: true
          }
        }
      };

      // Save request to Firebase
      const docRef = await addDoc(collection(db, 'requests'), requestData);
      setRequestId(docRef.id);
      
      // Create payment intent - NO SMS SENT YET
      console.log('Creating payment intent (no SMS yet)...');
      const functions = getFunctions();
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
      
      // CRITICAL: Make sure customer name is passed correctly
      const paymentData = {
        amount: Math.round(calculatedTotal * 100),
        currency: 'aud',
        songId: preSelectedSong?.id,
        musicianId: musicianId,
        customerName: customerName.trim(), // IMPORTANT: Customer name here!
        requestId: docRef.id,
        messageToMusician: message.trim() || null // Pass the message to musician
      };
      
      console.log('Payment intent data being sent:', paymentData);
      
      // Create Stripe Payment Intent - SMS will be sent by webhook AFTER authorization
      const result = await createPaymentIntent(paymentData);

      const secret = result.data.clientSecret;
      const paymentId = result.data.paymentIntentId;
      
      setClientSecret(secret);
      if (paymentId) {
        setPaymentIntentId(paymentId);
      }
      setShowPayment(true);
      
      console.log('Payment intent created successfully - awaiting customer payment authorization');
      console.log('Customer name in payment intent:', customerName.trim());
      
    } catch (error) {
      console.error('Error submitting request:', error);
      
      // Enhanced error handling
      if (error.code === 'functions/invalid-argument') {
        alert('Invalid request data. Please check your input and try again.');
      } else if (error.code === 'functions/not-found') {
        alert('Song or musician not found. Please refresh the page and try again.');
      } else if (error.code === 'functions/failed-precondition') {
        alert('Request validation failed. Please check the amount and try again.');
      } else {
        alert('Error processing request. Please try again.');
      }
      
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    console.log('✅ Payment authorized successfully:', paymentIntent.id);
    console.log('🎵 SMS should now be sent to musician via webhook');
    
    setPaymentIntentId(paymentIntent.id);
    
    // Save to localStorage for session recovery
    try {
      const pendingRating = {
        paymentIntentId: paymentIntent.id,
        requestId: requestId,
        songId: preSelectedSong?.id,
        songName: preSelectedSong?.name,
        songArtist: preSelectedSong?.artist,
        songPrice: preSelectedSong?.price,
        amount: totalAmount,
        musicianId: musicianId,
        musicianName: musicianName,
        timestamp: new Date().toISOString(),
        status: 'pending_rating'
      };
      
      console.log('📝 Preparing to save pending rating:', pendingRating);
      
      // Get existing pending ratings or create new array
      const existingPending = JSON.parse(localStorage.getItem('pendingRatings') || '[]');
      console.log('📦 Existing pending ratings:', existingPending);
      
      // Check if this payment intent already exists (prevent duplicates)
      const alreadyExists = existingPending.some(r => r.paymentIntentId === paymentIntent.id);
      if (!alreadyExists) {
        existingPending.push(pendingRating);
      } else {
        console.log('⚠️ Pending rating already exists for this payment intent, skipping duplicate');
      }
      
      // Keep only ratings from last 48 hours and remove duplicates
      const cutoffTime = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
      const filteredPending = existingPending.filter(r => r.timestamp > cutoffTime);
      
      // Remove duplicates based on paymentIntentId
      const uniquePending = filteredPending.filter((rating, index, self) =>
        index === self.findIndex(r => r.paymentIntentId === rating.paymentIntentId)
      );
      
      localStorage.setItem('pendingRatings', JSON.stringify(uniquePending));
      console.log('✅ Saved pending rating to localStorage for recovery');
      console.log('📋 Total pending ratings now:', uniquePending.length);
      
      // Verify it was saved
      const verification = localStorage.getItem('pendingRatings');
      console.log('🔍 Verification - localStorage now contains:', verification);
    } catch (error) {
      console.error('❌ Failed to save to localStorage:', error);
      // Continue anyway - not critical
    }
    
    // CRITICAL: Update request status to pending_rating for auto-capture to work
    if (requestId) {
      try {
        console.log('📝 Updating request status to pending_rating...');
        const requestRef = doc(db, 'requests', requestId);
        await updateDoc(requestRef, {
          status: 'pending_rating',
          paymentStatus: 'authorized',
          paymentIntentId: paymentIntent.id,
          baseAmount: totalAmount, // Store base amount for auto-capture
          createdAt: serverTimestamp(), // Add timestamp for auto-capture query
          updatedAt: serverTimestamp()
        });
        console.log('✅ Request status updated to pending_rating');
      } catch (error) {
        console.error('❌ Failed to update request status:', error);
        // Continue anyway - payment was successful
      }
    }
    
    setShowPayment(false);
    setIsSubmitting(false);
    
    // Trigger rating modal via parent component
    if (onPaymentAuthorized) {
      onPaymentAuthorized(preSelectedSong, paymentIntent.id, totalAmount);
    } else {
      // Fallback: dispatch custom event for parent to listen to
      window.dispatchEvent(new CustomEvent('showRatingModal', {
        detail: { 
          song: preSelectedSong, 
          paymentIntentId: paymentIntent.id, 
          amount: totalAmount 
        }
      }));
    }
  };

  const handlePaymentError = (error) => {
    console.error('❌ Payment failed:', error);
    console.log('🚫 No SMS sent to musician - payment was not authorized');
    
    if (error.type === 'card_error') {
      alert(`Card error: ${error.message}`);
    } else if (error.type === 'validation_error') {
      alert('Please check your card details and try again.');
    } else {
      alert('Payment failed. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const handlePaymentCancel = () => {
    console.log('🚫 Payment cancelled by user - no SMS sent to musician');
    setShowPayment(false);
    setIsSubmitting(false);
  };


  return (
    <div style={{ width: '100%' }}>
      {!showPayment ? (
        // Request Form
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Song Info Display */}
          <div style={{
            backgroundColor: '#374151',
            border: '3px solid #6B7280',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '20px' }}>🎵</span>
              <h3 style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                Song Request
              </h3>
            </div>
            <p style={{ color: '#D1D5DB', fontSize: '16px', margin: '0 0 4px 0' }}>
              <strong>{preSelectedSong?.name}</strong> by {preSelectedSong?.artist}
            </p>
            <p style={{ color: '#10B981', fontSize: '14px', fontWeight: 'bold', margin: 0 }}>
              Base Price: ${preSelectedSong?.price?.toFixed(2) || '0.00'}
            </p>
          </div>

          {/* Customer Name Input - CRITICAL FIELD */}
          <div>
            <label style={{
              display: 'block',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              Your Name *
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter your name (will be shown to musician)"
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#374151',
                border: '2px solid #6B7280',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '4px 0 0 0' }}>
              This name will be sent to the musician with your request
            </p>
          </div>

          {/* Optional Message */}
          <div>
            <label style={{
              display: 'block',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              Message to Musician (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Any special requests or dedications..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#374151',
                border: '2px solid #6B7280',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Tip Notice */}
          <div style={{
            backgroundColor: '#312E81',
            border: '2px solid #6366F1',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>💡</span>
              <div>
                <h4 style={{
                  color: '#C7D2FE',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  margin: '0 0 4px 0'
                }}>
                  Tipping After Performance
                </h4>
                <p style={{
                  color: '#C7D2FE',
                  fontSize: '13px',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  You can add a tip when rating the performance. We'll hold an extra $20 on your card to allow for tipping, but you'll only be charged the amount you choose.
                </p>
              </div>
            </div>
          </div>

          {/* Total Amount Display */}
          <div style={{
            backgroundColor: '#065F46',
            border: '3px solid #10B981',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ color: '#A7F3D0', fontSize: '14px', marginBottom: '4px' }}>
              Total Amount
            </div>
            <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
              ${calculatedTotal.toFixed(2)} AUD
            </div>
            <div style={{ color: '#A7F3D0', fontSize: '12px', marginTop: '4px' }}>
              Song Request Fee (Tip can be added after performance)
            </div>
          </div>

          {/* MOVED: Continue to Payment Button - NOW ABOVE Payment Protection */}
          <button
            type="submit"
            disabled={isSubmitting || !customerName.trim() || calculatedTotal <= 0}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: (isSubmitting || !customerName.trim() || calculatedTotal <= 0) ? '#4B5563' : '#10B981',
              border: `3px solid ${(isSubmitting || !customerName.trim() || calculatedTotal <= 0) ? '#6B7280' : '#059669'}`,
              borderRadius: '12px',
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: (isSubmitting || !customerName.trim() || calculatedTotal <= 0) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isSubmitting ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Preparing Payment...
              </>
            ) : (
              <>
                🔒 Continue to Payment - ${calculatedTotal.toFixed(2)}
              </>
            )}
          </button>

          {/* Payment Protection Notice - NOW BELOW the button */}
          <div style={{
            backgroundColor: '#1E40AF',
            border: '2px solid #3B82F6',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px', flexShrink: 0 }}>💳</span>
              <div>
                <h4 style={{
                  color: '#BFDBFE',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  margin: '0 0 8px 0'
                }}>
                  Payment Protection
                </h4>
                <p style={{
                  color: '#BFDBFE',
                  fontSize: '14px',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  Your payment is <strong>authorized but not charged</strong> until you rate the performance. 
                  If you're not satisfied, you can cancel with no charge.
                </p>
              </div>
            </div>
          </div>

          {/* Updated workflow explanation */}
          <div style={{
            backgroundColor: '#1E3A8A',
            border: '2px solid #3B82F6',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>📋</span>
              <div>
                <h4 style={{
                  color: '#BFDBFE',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  margin: '0 0 8px 0'
                }}>
                  Request Process
                </h4>
                <ol style={{
                  color: '#BFDBFE',
                  fontSize: '13px',
                  lineHeight: '1.4',
                  margin: 0,
                  paddingLeft: '16px'
                }}>
                  <li>Enter your payment details</li>
                  <li>Authorize payment (not charged yet)</li>
                  <li><strong>Musician gets SMS notification</strong> 🎵</li>
                  <li>Musician performs your song</li>
                  <li>Rate performance to complete payment</li>
                </ol>
              </div>
            </div>
          </div>

          {/* NEW: Legal Notice */}
          <div style={{
            backgroundColor: '#4B5563',
            border: '2px solid #6B7280',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>⚖️</span>
              <div>
                <p style={{
                  color: '#D1D5DB',
                  fontSize: '12px',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  By submitting this request, you agree to our{' '}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      color: '#7C3AED',
                      textDecoration: 'underline'
                    }}
                  >
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      color: '#7C3AED',
                      textDecoration: 'underline'
                    }}
                  >
                    Privacy Policy
                  </a>
                  . Your name will be shared with the musician for a personalized performance.
                </p>
              </div>
            </div>
          </div>

          {/* Security Notice - kept as was */}
          <div style={{
            backgroundColor: '#7F1D1D',
            border: '2px solid #DC2626',
            borderRadius: '12px',
            padding: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '16px' }}>🔒</span>
              <p style={{
                color: '#FCA5A5',
                fontSize: '12px',
                margin: 0,
                lineHeight: '1.3'
              }}>
                <strong>Payment Protection:</strong> Your payment is only processed when you rate the performance. 
                If you're not satisfied, you can cancel with no charge.
              </p>
            </div>
          </div>

        </form>
      ) : (
        // Payment Form
        <StripePaymentForm
          clientSecret={clientSecret}
          amount={Math.round(calculatedTotal * 100)}
          songName={preSelectedSong?.name}
          artistName={preSelectedSong?.artist}
          musicianName={musicianName}
          customerName={customerName}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onCancel={handlePaymentCancel}
        />
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default SongRequestForm;